import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ContactSection from '../components/ContactSection';
import {
  Cog,
  Wrench,
  Zap,
  Code,
  Globe,
  Users,
  Shield,
  ArrowRight,
  CheckCircle,
  MapPin,
  MessageSquare,
  BarChart3,
  Languages,
  Award,
  Star,
  Search,
  UserPlus,
  Handshake,
  TrendingUp,
  Quote,
  Cpu,
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useLanguage();
  const { isAuthenticated, isProfessional, hasPaid } = useAuth();
  const navigate = useNavigate();

  const specializations = [
    {
      key: 'mechatronics',
      icon: Cog,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      subs: ['sub_mechanical_electrical_assembly', 'sub_maintenance'],
    },
    {
      key: 'mechanical',
      icon: Wrench,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      subs: ['sub_mechanical_assembly', 'sub_maintenance', 'sub_mechanical_designer'],
    },
    {
      key: 'electrical',
      icon: Zap,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      subs: [
        'sub_switchboards',
        'sub_machine_line_edge',
        'sub_robotics',
        'sub_assembly',
        'sub_maintenance',
        'sub_electrical_designer',
      ],
    },
    {
      key: 'software',
      icon: Code,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      subs: [
        'sub_software_development',
        'sub_plc_programming',
        'sub_robotics_programming',
        'sub_support',
        'sub_ai_integration',
      ],
    },
  ];

  const regions = [
    { key: 'europe', flag: '🇪🇺' },
    { key: 'asia', flag: '🌏' },
    { key: 'america', flag: '🌎' },
    { key: 'south_america', flag: '🌎' },
    { key: 'africa', flag: '🌍' },
  ];

  const features = [
    { key: 'geo', icon: MapPin, color: 'text-blue-600', bg: 'bg-blue-50' },
    { key: 'chat', icon: MessageSquare, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { key: 'secure', icon: Shield, color: 'text-slate-700', bg: 'bg-slate-100' },
    { key: 'analytics', icon: BarChart3, color: 'text-orange-600', bg: 'bg-orange-50' },
    { key: 'multilang', icon: Languages, color: 'text-purple-600', bg: 'bg-purple-50' },
    { key: 'certs', icon: Award, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  const steps = [
    { key: 'step_1', icon: UserPlus, num: '01' },
    { key: 'step_2', icon: Search, num: '02' },
    { key: 'step_3', icon: Handshake, num: '03' },
  ];

  const testimonials = [
    {
      key: '1',
      avatar: 'MR',
      color: 'bg-blue-100 text-blue-700',
    },
    {
      key: '2',
      avatar: 'AK',
      color: 'bg-orange-100 text-orange-700',
    },
    {
      key: '3',
      avatar: 'SG',
      color: 'bg-emerald-100 text-emerald-700',
    },
  ];

  const handleGetStarted = () => {
    // Manufacturing company registration (was end_user free signup)
    if (isAuthenticated) {
      navigate('/directory');
    } else {
      navigate('/register?type=azienda_manifatturiera');
    }
  };

  const handleProfessionalJoin = () => {
    // Technician/freelance — completely free
    if (isAuthenticated && isProfessional) {
      navigate('/dashboard');
    } else {
      navigate('/register-professional');
    }
  };

  const handleAutomationCompanyJoin = () => {
    // Automation company (system integrator) — free signup, premium to unlock contacts
    if (isAuthenticated) {
      navigate('/directory');
    } else {
      navigate('/register?type=azienda_automazione');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50"
        data-testid="hero-section"
      >
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div
                className="inline-flex items-center gap-2 bg-white border border-orange-200 shadow-sm rounded-full px-4 py-1.5 mb-6"
                data-testid="hero-badge"
              >
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                <span className="text-orange-600 text-xs font-mono uppercase tracking-wider font-semibold">
                  {t('website_hero_badge')}
                </span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 text-slate-900"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {t('hero_title')}
              </h1>

              <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
                {t('hero_subtitle')}
              </p>

              <div className="flex flex-col gap-3 mb-10 max-w-md">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-md px-6 py-6 font-semibold shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-0.5 justify-start"
                  onClick={handleProfessionalJoin}
                  data-testid="hero-cta-pro"
                >
                  <span className="flex-1 text-left">
                    <span className="block text-base font-bold">{t('hero_cta_technician')}</span>
                    <span className="block text-xs font-normal opacity-90">{t('hero_cta_technician_sub')}</span>
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0" />
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-300 bg-white text-slate-900 hover:bg-slate-50 rounded-md px-6 py-6 font-semibold justify-start"
                  onClick={handleGetStarted}
                  data-testid="hero-cta-manufacturer"
                >
                  <span className="flex-1 text-left">
                    <span className="block text-base font-bold">{t('hero_cta_manufacturer')}</span>
                    <span className="block text-xs font-normal text-slate-500">{t('hero_cta_manufacturer_sub')}</span>
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0" />
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-300 bg-white text-slate-900 hover:bg-slate-50 rounded-md px-6 py-6 font-semibold justify-start"
                  onClick={handleAutomationCompanyJoin}
                  data-testid="hero-cta-automation"
                >
                  <span className="flex-1 text-left">
                    <span className="block text-base font-bold">{t('hero_cta_automation')}</span>
                    <span className="block text-xs font-normal text-slate-500">{t('hero_cta_automation_sub')}</span>
                  </span>
                  <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0" />
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>{t('website_trust_payment')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>{t('website_trust_languages')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>{t('website_trust_global')}</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10 border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="Industrial automation"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 via-transparent to-transparent" />
              </div>

              {/* Floating Card 1 */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">500+</p>
                    <p className="text-xs text-slate-500 font-medium">
                      {t('website_stats_pros_label')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating Card 2 */}
              <div className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Star className="h-6 w-6 text-orange-600 fill-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">4.9</p>
                    <p className="text-xs text-slate-500 font-medium">
                      {t('website_stats_rating_label')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section className="bg-slate-900 text-white py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">500+</p>
              <p className="text-sm text-slate-400 uppercase tracking-wider font-medium">
                {t('website_stats_pros')}
              </p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">25+</p>
              <p className="text-sm text-slate-400 uppercase tracking-wider font-medium">
                {t('website_stats_countries')}
              </p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">4</p>
              <p className="text-sm text-slate-400 uppercase tracking-wider font-medium">
                {t('website_stats_specs')}
              </p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">98%</p>
              <p className="text-sm text-slate-400 uppercase tracking-wider font-medium">
                {t('website_stats_satisfaction')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1565008576549-57569a49371d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Control room automation"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-orange-500 text-white p-6 rounded-xl shadow-lg max-w-xs hidden md:block">
                <TrendingUp className="h-8 w-8 mb-2" />
                <p className="font-semibold text-sm">{t('about_callout')}</p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
                {t('about_badge')}
              </span>
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6 leading-tight"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {t('about_title')}
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">{t('about_text_1')}</p>
              <p className="text-base text-slate-600 mb-8 leading-relaxed">{t('about_text_2')}</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{t('about_bullet_1')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{t('about_bullet_2')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{t('about_bullet_3')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
              {t('how_badge')}
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('how_title')}
            </h2>
            <p className="text-lg text-slate-600">{t('how_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, idx) => (
              <div key={step.key} className="relative" data-testid={`step-${step.key}`}>
                <Card className="border border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all rounded-xl bg-white h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-14 h-14 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center">
                        <step.icon className="h-7 w-7 text-orange-600" />
                      </div>
                      <span className="text-5xl font-bold text-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {step.num}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {t(`how_${step.key}_title`)}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {t(`how_${step.key}_desc`)}
                    </p>
                  </CardContent>
                </Card>
                {idx < steps.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10 bg-white border border-slate-200 rounded-full w-8 h-8 items-center justify-center shadow-sm">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SPECIALIZATIONS ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="specializations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
              {t('pro_specializations')}
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('specs_title')}
            </h2>
            <p className="text-lg text-slate-600">{t('specs_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specializations.map((spec) => (
              <Card
                key={spec.key}
                className="border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all rounded-xl group"
                data-testid={`spec-card-${spec.key}`}
              >
                <CardContent className="p-6">
                  <div
                    className={`w-14 h-14 ${spec.bgColor} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
                  >
                    <spec.icon className={`h-7 w-7 ${spec.color}`} />
                  </div>
                  <h3
                    className="text-lg font-bold text-slate-900 mb-3"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {t(`spec_${spec.key}`)}
                  </h3>
                  <ul className="space-y-2">
                    {spec.subs.map((sub) => (
                      <li
                        key={sub}
                        className="text-sm text-slate-600 flex items-start gap-2"
                      >
                        <span className="text-orange-500 mt-1 font-bold">•</span>
                        {t(sub)}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
              {t('features_badge')}
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('features_title')}
            </h2>
            <p className="text-lg text-slate-600">{t('features_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat) => (
              <div
                key={feat.key}
                className="bg-white p-8 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all"
                data-testid={`feature-${feat.key}`}
              >
                <div
                  className={`w-12 h-12 ${feat.bg} rounded-lg flex items-center justify-center mb-5`}
                >
                  <feat.icon className={`h-6 w-6 ${feat.color}`} />
                </div>
                <h3
                  className="text-lg font-bold text-slate-900 mb-3"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {t(`feature_${feat.key}_title`)}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  {t(`feature_${feat.key}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING SECTION ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
              {t('pricing_badge')}
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('pricing_title')}
            </h2>
            <p className="text-lg text-slate-600">{t('pricing_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Manufacturing Company Card */}
            <Card
              className="border-2 border-slate-200 rounded-2xl bg-white relative hover:shadow-xl transition-all"
              data-testid="pricing-card-user"
            >
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <Search className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3
                    className="text-2xl font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {t('pricing_user_title')}
                  </h3>
                  <p className="text-slate-600 text-sm">{t('pricing_user_desc')}</p>
                </div>

                <div className="mb-6 pb-6 border-b border-slate-100">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-5xl font-bold text-slate-900"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {t('pay_amount')}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pay_features_1')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pay_features_2')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pay_features_3')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pay_features_4')}</span>
                  </li>
                </ul>

                <Button
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-md py-6 font-semibold"
                  onClick={handleGetStarted}
                  data-testid="pricing-user-cta"
                >
                  {t('pricing_user_cta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Professional Card (FEATURED) */}
            <Card
              className="border-2 border-orange-500 rounded-2xl bg-gradient-to-br from-white to-orange-50/30 relative hover:shadow-xl transition-all shadow-lg md:scale-105"
              data-testid="pricing-card-pro"
            >
              <Badge className="absolute -top-3 right-6 bg-orange-500 hover:bg-orange-500 text-white px-3 py-1 text-xs font-semibold">
                {t('pricing_pro_badge')}
              </Badge>
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <UserPlus className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3
                    className="text-2xl font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {t('pricing_pro_title')}
                  </h3>
                  <p className="text-slate-600 text-sm">{t('pricing_pro_desc')}</p>
                </div>

                <div className="mb-6 pb-6 border-b border-orange-100">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-5xl font-bold text-slate-900"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {t('pay_amount')}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_pro_feat_1')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_pro_feat_2')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_pro_feat_3')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_pro_feat_4')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_pro_feat_5')}</span>
                  </li>
                </ul>

                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-md py-6 font-semibold shadow-lg shadow-orange-500/20"
                  onClick={handleProfessionalJoin}
                  data-testid="pricing-pro-cta"
                >
                  {t('pricing_pro_cta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Automation Company Card */}
            <Card
              className="border-2 border-slate-200 rounded-2xl bg-white relative hover:shadow-xl transition-all"
              data-testid="pricing-card-automation"
            >
              <CardContent className="p-8">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-indigo-50 rounded-lg flex items-center justify-center mb-4">
                    <Cpu className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3
                    className="text-2xl font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {t('pricing_automation_title')}
                  </h3>
                  <p className="text-slate-600 text-sm">{t('pricing_automation_desc')}</p>
                </div>

                <div className="mb-6 pb-6 border-b border-slate-100">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-5xl font-bold text-slate-900"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {t('pay_amount')}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_automation_feat_1')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_automation_feat_2')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_automation_feat_3')}</span>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <CheckCircle className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{t('pricing_automation_feat_4')}</span>
                  </li>
                </ul>

                <Button
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-md py-6 font-semibold"
                  onClick={handleAutomationCompanyJoin}
                  data-testid="pricing-automation-cta"
                >
                  {t('pricing_automation_cta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Renewal section removed — new B2B Freemium model: no yearly renewals.
              Subscription pricing for Premium (29€/m) and Business (39€/m) will be added in Phase 3. */}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
              {t('testimonials_badge')}
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('testimonials_title')}
            </h2>
            <p className="text-lg text-slate-600">{t('testimonials_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tt) => (
              <Card
                key={tt.key}
                className="border border-slate-200 rounded-xl bg-white hover:shadow-lg transition-all"
                data-testid={`testimonial-${tt.key}`}
              >
                <CardContent className="p-8">
                  <Quote className="h-8 w-8 text-orange-500/20 mb-4" />
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 leading-relaxed mb-6 italic">
                    "{t(`testimonial_${tt.key}_text`)}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                    <div
                      className={`w-12 h-12 ${tt.color} rounded-full flex items-center justify-center font-bold text-sm`}
                    >
                      {tt.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">
                        {t(`testimonial_${tt.key}_name`)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t(`testimonial_${tt.key}_role`)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== REGIONS ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="regions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <span className="text-xs font-mono uppercase tracking-widest text-orange-600 mb-4 block font-semibold">
              {t('regions_badge')}
            </span>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('regions_title')}
            </h2>
            <p className="text-lg text-slate-600">{t('regions_subtitle')}</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {regions.map((region) => (
              <div
                key={region.key}
                className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-6 py-4 hover:border-orange-400 hover:bg-white hover:shadow-md transition-all"
                data-testid={`region-${region.key}`}
              >
                <span className="text-2xl">{region.flag}</span>
                <span className="font-semibold text-slate-900">
                  {t(`region_${region.key}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <ContactSection />

      {/* ===== FINAL CTA ===== */}
      <section
        className="relative overflow-hidden bg-slate-900 text-white py-20 md:py-28"
        data-testid="cta-section"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(#fb923c 1px, transparent 1px), linear-gradient(90deg, #fb923c 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-orange-400 text-xs font-mono uppercase tracking-wider font-semibold">
              {t('final_cta_badge')}
            </span>
          </div>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6 leading-tight"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('final_cta_title')}
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            {t('final_cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-md px-8 py-6 font-semibold shadow-lg shadow-orange-500/30"
              onClick={handleGetStarted}
              data-testid="cta-get-started"
            >
              {t('hero_cta_user')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-800 rounded-md px-8 py-6 font-semibold bg-transparent"
              onClick={handleProfessionalJoin}
              data-testid="cta-pro-join"
            >
              {t('hero_cta_pro')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
